import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Menu, Popover } from 'antd';
import { withRouter, matchPath } from 'react-router';
import { withTranslation } from 'react-i18next';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import cn from 'classnames';
import _ from 'lodash';
import Immutable from 'immutable';

import { connect } from '../../../../../../../StateProvider';
import { classifyViews } from '../../../../../../../../utils/classifyViews';
import {
  LEFT_PANEL_TABS,
  VIEW_MODE_COLORS,
  VIEW_MODE_ICONS,
  VIEW_MODES,
  resolveDefaultViewId,
} from '../../../../../../../../configs/viewSettings';
import { getViewModeFromPath } from '../../../../../../../../utils/viewSettings';
import routes, { VIEW_MODE_ROUTES } from '../../../../../../../../routes';
import getLink from '../../../../../../../common/router/getLink';
import StateRedirect from '../../../../../../../common/router/StateRedirect';
import DefaultRedirect from '../../../../../../../common/router/DefaultRedirect';
import NavRoute from '../../../../../../../common/router/Route';
import ViewStoreActions from '../../../../../../../../actions/viewActions';
import Actions from '../../Actions';
import filterActions from '../../../../../../../../actions/filterActions';
import PRIVILEGE_CODES from '../../../../../../../../configs/privilegeCodes';
import { decode } from '../../../../../../../../utils/queryParams';

import styles from './viewsMenu.less';

const ViewsMenu = ({
  mode,
  sceneId,
  catalogId,
  catalog,
  viewId,
  views,
  viewsLoaded,
  scenes,
  viewSettings,
  isNewViewExist,
  catalogPrivilegeCode,
  defaultViewId,
  history,
  location,
  match,
  onTabChange,
  className,
  t,
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [foreignExpanded, setForeignExpanded] = useState(false);

  const { commonViews, foreignRightsViews } = useMemo(() => classifyViews(views), [views]);

  const viewMatch = matchPath(location.pathname, { path: routes.view.path, exact: false });
  const currentViewId = _.get(viewMatch, ['params', 'viewId']);

  const activeViewMode = getViewModeFromPath(location.pathname);

  useEffect(() => {
    if (foreignRightsViews && foreignRightsViews.some((v) => v.get('id') === currentViewId)) {
      setForeignExpanded(true);
    }
  }, [currentViewId, foreignRightsViews]);

  const getDefaultView = () => {
    const matchViewId = _.get(match, ['params', 'viewId']);
    if (matchViewId && (matchViewId !== '$new' || isNewViewExist)) {
      return Immutable.Map({ id: matchViewId });
    }
    const id = resolveDefaultViewId(views, viewsLoaded, defaultViewId);
    return id ? Immutable.Map({ id }) : null;
  };

  const getViewHasChanges = (viewId) => {
    const filtersChanged = scenes && scenes.getIn([sceneId, 'views', viewId, 'filtersChanged']);
    const settingsChanged = viewSettings && viewSettings.getIn([String(catalogId), 'views', viewId, 'hasChanges']);
    return filtersChanged || settingsChanged;
  };

  const handleSelect = (selectedViewId) => {
    localStorage.setItem(`catalog.${catalogId}.defaultViewId`, selectedViewId);
    if (isNewViewExist && selectedViewId !== '$new') {
      ViewStoreActions.truncateView({ catalogId, sceneId, viewId: '$new' });
    }
    const view = views && views.find((v) => v.get('id') === selectedViewId);

    const viewMode = (view && view.get('viewMode')) || VIEW_MODES.TABLE;
    const link = getLink(location, VIEW_MODE_ROUTES[viewMode] || VIEW_MODE_ROUTES[VIEW_MODES.TABLE], {
      viewId: selectedViewId,
    });
    history.push(link.pathname);
    if (mode === 'dropdown') setDropdownOpen(false);
  };

  const onCreateNewView = () => {
    filterActions.updateFieldFilters({ sceneId, catalogId, viewId: '0' }, Immutable.Map());
    history.push(getLink(location, routes.view, { viewId: '$new' }));
    onTabChange && onTabChange(LEFT_PANEL_TABS.FILTERS, false);
  };

  const canCreateView = currentViewId !== '$new';

  const buildViewItem = (view) => {
    const viewId = view.get('id');
    const vm = view.get('viewMode') || VIEW_MODES.TABLE;
    const Icon = VIEW_MODE_ICONS[vm] || VIEW_MODE_ICONS[VIEW_MODES.TABLE];
    const hasChanges = getViewHasChanges(viewId);

    return {
      key: viewId,
      icon: <Icon style={{ color: VIEW_MODE_COLORS[vm] }} className={styles.viewModeIcon} />,
      label: (
        <div className={styles.itemLabel}>
          <span className={styles.itemName}>{view.get('displayName')}</span>
          {mode === 'menu' ? (
            <span
              className={cn(styles.viewsActivities, hasChanges && styles.viewsActivitiesChanged)}
              onClick={(e) => e.stopPropagation()}
            >
              <Actions
                modes={['view']}
                sceneId={sceneId}
                catalogId={catalogId}
                viewId={viewId}
                catalog={catalog}
                title={t('views.actionsButton')}
              />
            </span>
          ) : null}
        </div>
      ),
    };
  };

  const buildItems = () => {
    const items = commonViews.map(buildViewItem).toArray();

    if (canCreateView) {
      items.push({
        key: '__create',
        icon: <PlusOutlined className={styles.newViewIcon} />,
        label: <span className={styles.newViewLink}>{t('views.newView')}</span>,
      });
    }

    if (foreignRightsViews && foreignRightsViews.size > 0) {
      items.push({
        key: '__foreignHeader',
        className: 'viewsMenu-foreignHeader',
        label: (
          <div className={cn(styles.foreignHeaderLabel, foreignExpanded && styles.foreignHeaderLabelExpanded)}>
            <DownOutlined className={cn(styles.foreignArrow, foreignExpanded && styles.foreignArrowOpen)} />
            <span>
              {t('views.list.foreignRightsViews')} <b>{foreignRightsViews.size}</b>
            </span>
          </div>
        ),
      });
      if (foreignExpanded) {
        foreignRightsViews
          .map(buildViewItem)
          .toArray()
          .forEach((item) => {
            const isActive = String(item.key) === String(currentViewId);
            items.push({
              ...item,
              className: isActive
                ? 'viewsMenu-foreignViewItem viewsMenu-foreignViewItemActive'
                : 'viewsMenu-foreignViewItem',
            });
          });
      }
    }

    return items;
  };

  const handleMenuClick = ({ key }) => {
    if (key === '__create') {
      onCreateNewView();
    } else if (key === '__foreignHeader') {
      setForeignExpanded((prev) => !prev);
    } else {
      handleSelect(key);
    }
  };

  const items = buildItems();

  const menuNode = (
    <Menu
      mode="inline"
      inlineIndent={0}
      selectedKeys={currentViewId ? [currentViewId] : []}
      items={items}
      onClick={handleMenuClick}
      className={styles.viewsMenu}
    />
  );

  if (mode === 'dropdown') {
    const selectedView =
      (commonViews && commonViews.find((v) => v.get('id') === currentViewId)) ||
      (foreignRightsViews && foreignRightsViews.find((v) => v.get('id') === currentViewId));
    const SelectedIcon = VIEW_MODE_ICONS[activeViewMode] || VIEW_MODE_ICONS[VIEW_MODES.TABLE];

    return (
      <>
        {isNewViewExist && currentViewId !== '$new' && (
          <StateRedirect route={routes.view} params={{ viewId: '$new' }} />
        )}
        <Popover
          open={dropdownOpen}
          onOpenChange={setDropdownOpen}
          trigger={['click']}
          placement="bottomLeft"
          overlayClassName={cn('ant-dropdown-menu ant-dropdown-menu-vertical', styles.dropdownPopup)}
          showArrow={false}
          getPopupContainer={(trigger) => trigger.parentElement}
          content={menuNode}
        >
          <div className={cn(styles.viewSelector, dropdownOpen && styles.viewSelectorOpen, className)}>
            <SelectedIcon className={styles.selectorIcon} style={{ color: VIEW_MODE_COLORS[activeViewMode] }} />
            <h2 className={styles.selectorName}>{selectedView && selectedView.get('displayName')}</h2>
            <DownOutlined className={cn(styles.selectorArrow, dropdownOpen && styles.selectorArrowOpen)} />
          </div>
        </Popover>
      </>
    );
  }

  return (
    <div className={styles.container}>
      <DefaultRedirect route={routes.view} params="viewId" object={getDefaultView()} />
      <NavRoute route={routes.view}>
        {({ match: routeMatch, location: routeLocation }) => {
          const matchedViewId = _.get(routeMatch, ['params', 'viewId']);
          const viewExist = views && matchedViewId && views.find((v) => v.get('id') === matchedViewId);
          const locationParams = routeLocation && decode(routeLocation.search);
          const locationFilters = locationParams && locationParams.filters;
          const filtersExist = !_.isEmpty(locationFilters);
          const isCreatingNewView = matchedViewId === '$new' && isNewViewExist;
          if (views && !viewExist && matchedViewId !== '0' && !filtersExist && !isCreatingNewView) {
            const lsDefaultViewId = localStorage.getItem(`catalog.${catalogId}.defaultViewId`) || '0';
            if (String(lsDefaultViewId) === String(matchedViewId)) {
              return null;
            }

            return <StateRedirect route={routes.view} params={{ viewId: '0' }} />;
          }
          return null;
        }}
      </NavRoute>
      {menuNode}
    </div>
  );
};

ViewsMenu.defaultProps = {
  mode: 'menu',
};

ViewsMenu.propTypes = {
  mode: PropTypes.oneOf(['menu', 'dropdown']),
  sceneId: PropTypes.string,
  catalogId: PropTypes.string,
  catalog: PropTypes.object,
  viewId: PropTypes.string,
  className: PropTypes.string,
  onTabChange: PropTypes.func,
};

export default withTranslation()(
  withRouter(
    connect(
      ViewsMenu,
      {
        scenes: ['scenes'],
        viewSettings: ['viewSettings', 'catalogs'],
      },
      (props, { scenes, viewSettings }) => {
        const { catalog, sceneId } = props;
        const scene = scenes.get(sceneId);
        const sceneViews = scenes.getIn([sceneId, 'views']) || Immutable.Map();
        let views = catalog && catalog.get('views');
        const viewsLoaded = catalog && catalog.get('viewsLoaded');
        views = viewsLoaded && views && views.merge(sceneViews);
        const catalogId = catalog && catalog.get('id');
        const defaultViewId = localStorage.getItem(`catalog.${catalogId}.defaultViewId`);
        const catalogPrivilegeCode = catalog && catalog.get('privilegeCode');
        const isNewViewExist = views && views.some((v) => v.get('id') === '$new');

        return {
          ...props,
          scene,
          views,
          viewsLoaded,
          catalogId,
          defaultViewId,
          catalogPrivilegeCode,
          isNewViewExist,
          scenes,
          viewSettings,
        };
      },
    ),
  ),
);
